/**
 * Generated by orval 🍺
 * Do not edit manually.
 * SolidOps API
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  AugmentedZodDtoOutput,
  EligibleScheduleAgentsResponseOutput,
  OverrideDetailResponseOutput,
  OverrideMutationResponseOutput,
  OverrideTimeOptionsResponseOutput,
  OwnDutyResponseOutput,
  OwnScheduleTeamsResponseOutput,
  RoutineChangePreviewResponseOutput,
  RoutineCreatePreviewResponseOutput,
  RoutineMutationResponseOutput,
  ScheduleArchivePreviewResponseOutput,
  ScheduleDetailResponseOutput,
  ScheduleMutationResponseOutput,
  SchedulePageResponseOutput,
  ScheduleSummaryResponseOutput,
  TimelineResponseOutput,
} from '../model';
import type {
  GetOverrideTimeOptionsParams,
  GetOwnDutyCalendarParams,
  GetOwnDutyParams,
  GetScheduleTimelineParams,
  GetTeamDutyParams,
  GetTeamTimelineParams,
  ListTeamSchedulesParams,
  OverrideCancelBody,
  OverrideCreateBody,
  OverrideUpdateBody,
  RoutineChangeConfirmBody,
  RoutineChangePreviewBody,
  RoutineCreateBody,
  RoutineCreatePreviewBody,
  RoutinePendingCancelBody,
  ScheduleArchiveBody,
  ScheduleArchivePreviewBody,
  ScheduleCreateBody,
  ScheduleRenameBody,
} from '../model';

import { validatedFetch } from '../../client';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getListTeamSchedulesUrl = (teamId: string, params?: ListTeamSchedulesParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/teams/${teamId}/schedules?${stringifiedParams}`
    : `/api/v1/teams/${teamId}/schedules`;
};

export const listTeamSchedules = async (
  teamId: string,
  params?: ListTeamSchedulesParams,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<SchedulePageResponseOutput> => {
  return validatedFetch<SchedulePageResponseOutput>(getListTeamSchedulesUrl(teamId, params), {
    ...options,
    method: 'GET',

    schema: SchedulePageResponseOutput,
  });
};

export const getListTeamSchedulesQueryKey = (teamId: string, params?: ListTeamSchedulesParams) => {
  return [`/api/v1/teams/${teamId}/schedules`, ...(params ? [params] : [])] as const;
};

export const getListTeamSchedulesQueryOptions = <
  TData = Awaited<ReturnType<typeof listTeamSchedules>>,
  TError = unknown,
>(
  teamId: string,
  params?: ListTeamSchedulesParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listTeamSchedules>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListTeamSchedulesQueryKey(teamId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listTeamSchedules>>> = ({ signal }) =>
    listTeamSchedules(teamId, params, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: teamId !== null && teamId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listTeamSchedules>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type ListTeamSchedulesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listTeamSchedules>>
>;
export type ListTeamSchedulesQueryError = unknown;

export function useListTeamSchedules<
  TData = Awaited<ReturnType<typeof listTeamSchedules>>,
  TError = unknown,
>(
  teamId: string,
  params?: ListTeamSchedulesParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listTeamSchedules>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListTeamSchedulesQueryOptions(teamId, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getCreateScheduleUrl = (teamId: string) => {
  return `/api/v1/teams/${teamId}/schedules`;
};

export const createSchedule = async (
  teamId: string,
  scheduleCreateBody: ScheduleCreateBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<ScheduleMutationResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<ScheduleMutationResponseOutput>(getCreateScheduleUrl(teamId), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(scheduleCreateBody),
    schema: ScheduleMutationResponseOutput,
  });
};

export const getCreateScheduleMutationKey = () => ['createSchedule'] as const;

export const getCreateScheduleMutationOptions = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSchedule>>,
    TError,
    CreateScheduleMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createSchedule>>,
  TError,
  CreateScheduleMutationVariables,
  TContext
> => {
  const mutationKey = getCreateScheduleMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createSchedule>>,
    CreateScheduleMutationVariables
  > = (props) => {
    const { teamId, data } = props ?? {};

    return createSchedule(teamId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateScheduleMutationResult = NonNullable<Awaited<ReturnType<typeof createSchedule>>>;
export type CreateScheduleMutationBody = ScheduleCreateBody;
export type CreateScheduleMutationError = unknown;
export type CreateScheduleMutationVariables = { teamId: string; data: ScheduleCreateBody };

export const useCreateSchedule = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSchedule>>,
    TError,
    CreateScheduleMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createSchedule>>,
  TError,
  CreateScheduleMutationVariables,
  TContext
> => {
  return useMutation(getCreateScheduleMutationOptions(options));
};
export const getGetScheduleUrl = (scheduleId: string) => {
  return `/api/v1/schedules/${scheduleId}`;
};

export const getSchedule = async (
  scheduleId: string,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<ScheduleDetailResponseOutput> => {
  return validatedFetch<ScheduleDetailResponseOutput>(getGetScheduleUrl(scheduleId), {
    ...options,
    method: 'GET',

    schema: ScheduleDetailResponseOutput,
  });
};

export const getGetScheduleQueryKey = (scheduleId: string) => {
  return [`/api/v1/schedules/${scheduleId}`] as const;
};

export const getGetScheduleQueryOptions = <
  TData = Awaited<ReturnType<typeof getSchedule>>,
  TError = unknown,
>(
  scheduleId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getSchedule>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetScheduleQueryKey(scheduleId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSchedule>>> = ({ signal }) =>
    getSchedule(scheduleId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: scheduleId !== null && scheduleId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getSchedule>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetScheduleQueryResult = NonNullable<Awaited<ReturnType<typeof getSchedule>>>;
export type GetScheduleQueryError = unknown;

export function useGetSchedule<TData = Awaited<ReturnType<typeof getSchedule>>, TError = unknown>(
  scheduleId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getSchedule>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetScheduleQueryOptions(scheduleId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getRenameScheduleUrl = (scheduleId: string) => {
  return `/api/v1/schedules/${scheduleId}`;
};

export const renameSchedule = async (
  scheduleId: string,
  scheduleRenameBody: ScheduleRenameBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<ScheduleSummaryResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<ScheduleSummaryResponseOutput>(getRenameScheduleUrl(scheduleId), {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(scheduleRenameBody),
    schema: ScheduleSummaryResponseOutput,
  });
};

export const getRenameScheduleMutationKey = () => ['renameSchedule'] as const;

export const getRenameScheduleMutationOptions = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof renameSchedule>>,
    TError,
    RenameScheduleMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof renameSchedule>>,
  TError,
  RenameScheduleMutationVariables,
  TContext
> => {
  const mutationKey = getRenameScheduleMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof renameSchedule>>,
    RenameScheduleMutationVariables
  > = (props) => {
    const { scheduleId, data } = props ?? {};

    return renameSchedule(scheduleId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RenameScheduleMutationResult = NonNullable<Awaited<ReturnType<typeof renameSchedule>>>;
export type RenameScheduleMutationBody = ScheduleRenameBody;
export type RenameScheduleMutationError = unknown;
export type RenameScheduleMutationVariables = { scheduleId: string; data: ScheduleRenameBody };

export const useRenameSchedule = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof renameSchedule>>,
    TError,
    RenameScheduleMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof renameSchedule>>,
  TError,
  RenameScheduleMutationVariables,
  TContext
> => {
  return useMutation(getRenameScheduleMutationOptions(options));
};
export const getPreviewScheduleArchiveUrl = (scheduleId: string) => {
  return `/api/v1/schedules/${scheduleId}/archive-preview`;
};

export const previewScheduleArchive = async (
  scheduleId: string,
  scheduleArchivePreviewBody: ScheduleArchivePreviewBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<ScheduleArchivePreviewResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<ScheduleArchivePreviewResponseOutput>(
    getPreviewScheduleArchiveUrl(scheduleId),
    {
      ...options,
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
      body: JSON.stringify(scheduleArchivePreviewBody),
      schema: ScheduleArchivePreviewResponseOutput,
    },
  );
};

export const getPreviewScheduleArchiveMutationKey = () => ['previewScheduleArchive'] as const;

export const getPreviewScheduleArchiveMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof previewScheduleArchive>>,
    TError,
    PreviewScheduleArchiveMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof previewScheduleArchive>>,
  TError,
  PreviewScheduleArchiveMutationVariables,
  TContext
> => {
  const mutationKey = getPreviewScheduleArchiveMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof previewScheduleArchive>>,
    PreviewScheduleArchiveMutationVariables
  > = (props) => {
    const { scheduleId, data } = props ?? {};

    return previewScheduleArchive(scheduleId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PreviewScheduleArchiveMutationResult = NonNullable<
  Awaited<ReturnType<typeof previewScheduleArchive>>
>;
export type PreviewScheduleArchiveMutationBody = ScheduleArchivePreviewBody;
export type PreviewScheduleArchiveMutationError = unknown;
export type PreviewScheduleArchiveMutationVariables = {
  scheduleId: string;
  data: ScheduleArchivePreviewBody;
};

export const usePreviewScheduleArchive = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof previewScheduleArchive>>,
    TError,
    PreviewScheduleArchiveMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof previewScheduleArchive>>,
  TError,
  PreviewScheduleArchiveMutationVariables,
  TContext
> => {
  return useMutation(getPreviewScheduleArchiveMutationOptions(options));
};
export const getArchiveScheduleUrl = (scheduleId: string) => {
  return `/api/v1/schedules/${scheduleId}/archive`;
};

export const archiveSchedule = async (
  scheduleId: string,
  scheduleArchiveBody: ScheduleArchiveBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<ScheduleMutationResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<ScheduleMutationResponseOutput>(getArchiveScheduleUrl(scheduleId), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(scheduleArchiveBody),
    schema: ScheduleMutationResponseOutput,
  });
};

export const getArchiveScheduleMutationKey = () => ['archiveSchedule'] as const;

export const getArchiveScheduleMutationOptions = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof archiveSchedule>>,
    TError,
    ArchiveScheduleMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof archiveSchedule>>,
  TError,
  ArchiveScheduleMutationVariables,
  TContext
> => {
  const mutationKey = getArchiveScheduleMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof archiveSchedule>>,
    ArchiveScheduleMutationVariables
  > = (props) => {
    const { scheduleId, data } = props ?? {};

    return archiveSchedule(scheduleId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ArchiveScheduleMutationResult = NonNullable<
  Awaited<ReturnType<typeof archiveSchedule>>
>;
export type ArchiveScheduleMutationBody = ScheduleArchiveBody;
export type ArchiveScheduleMutationError = unknown;
export type ArchiveScheduleMutationVariables = { scheduleId: string; data: ScheduleArchiveBody };

export const useArchiveSchedule = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof archiveSchedule>>,
    TError,
    ArchiveScheduleMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof archiveSchedule>>,
  TError,
  ArchiveScheduleMutationVariables,
  TContext
> => {
  return useMutation(getArchiveScheduleMutationOptions(options));
};
export const getPreviewRoutineCreateUrl = (scheduleId: string) => {
  return `/api/v1/schedules/${scheduleId}/routines/create-preview`;
};

export const previewRoutineCreate = async (
  scheduleId: string,
  routineCreatePreviewBody: RoutineCreatePreviewBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<RoutineCreatePreviewResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<RoutineCreatePreviewResponseOutput>(
    getPreviewRoutineCreateUrl(scheduleId),
    {
      ...options,
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
      body: JSON.stringify(routineCreatePreviewBody),
      schema: RoutineCreatePreviewResponseOutput,
    },
  );
};

export const getPreviewRoutineCreateMutationKey = () => ['previewRoutineCreate'] as const;

export const getPreviewRoutineCreateMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof previewRoutineCreate>>,
    TError,
    PreviewRoutineCreateMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof previewRoutineCreate>>,
  TError,
  PreviewRoutineCreateMutationVariables,
  TContext
> => {
  const mutationKey = getPreviewRoutineCreateMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof previewRoutineCreate>>,
    PreviewRoutineCreateMutationVariables
  > = (props) => {
    const { scheduleId, data } = props ?? {};

    return previewRoutineCreate(scheduleId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PreviewRoutineCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof previewRoutineCreate>>
>;
export type PreviewRoutineCreateMutationBody = RoutineCreatePreviewBody;
export type PreviewRoutineCreateMutationError = unknown;
export type PreviewRoutineCreateMutationVariables = {
  scheduleId: string;
  data: RoutineCreatePreviewBody;
};

export const usePreviewRoutineCreate = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof previewRoutineCreate>>,
    TError,
    PreviewRoutineCreateMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof previewRoutineCreate>>,
  TError,
  PreviewRoutineCreateMutationVariables,
  TContext
> => {
  return useMutation(getPreviewRoutineCreateMutationOptions(options));
};
export const getCreateRoutineUrl = (scheduleId: string) => {
  return `/api/v1/schedules/${scheduleId}/routines`;
};

export const createRoutine = async (
  scheduleId: string,
  routineCreateBody: RoutineCreateBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<RoutineMutationResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<RoutineMutationResponseOutput>(getCreateRoutineUrl(scheduleId), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(routineCreateBody),
    schema: RoutineMutationResponseOutput,
  });
};

export const getCreateRoutineMutationKey = () => ['createRoutine'] as const;

export const getCreateRoutineMutationOptions = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createRoutine>>,
    TError,
    CreateRoutineMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createRoutine>>,
  TError,
  CreateRoutineMutationVariables,
  TContext
> => {
  const mutationKey = getCreateRoutineMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createRoutine>>,
    CreateRoutineMutationVariables
  > = (props) => {
    const { scheduleId, data } = props ?? {};

    return createRoutine(scheduleId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateRoutineMutationResult = NonNullable<Awaited<ReturnType<typeof createRoutine>>>;
export type CreateRoutineMutationBody = RoutineCreateBody;
export type CreateRoutineMutationError = unknown;
export type CreateRoutineMutationVariables = { scheduleId: string; data: RoutineCreateBody };

export const useCreateRoutine = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createRoutine>>,
    TError,
    CreateRoutineMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createRoutine>>,
  TError,
  CreateRoutineMutationVariables,
  TContext
> => {
  return useMutation(getCreateRoutineMutationOptions(options));
};
export const getPreviewRoutineChangeUrl = (routineId: string) => {
  return `/api/v1/routines/${routineId}/change-preview`;
};

export const previewRoutineChange = async (
  routineId: string,
  routineChangePreviewBody: RoutineChangePreviewBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<RoutineChangePreviewResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<RoutineChangePreviewResponseOutput>(getPreviewRoutineChangeUrl(routineId), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(routineChangePreviewBody),
    schema: RoutineChangePreviewResponseOutput,
  });
};

export const getPreviewRoutineChangeMutationKey = () => ['previewRoutineChange'] as const;

export const getPreviewRoutineChangeMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof previewRoutineChange>>,
    TError,
    PreviewRoutineChangeMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof previewRoutineChange>>,
  TError,
  PreviewRoutineChangeMutationVariables,
  TContext
> => {
  const mutationKey = getPreviewRoutineChangeMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof previewRoutineChange>>,
    PreviewRoutineChangeMutationVariables
  > = (props) => {
    const { routineId, data } = props ?? {};

    return previewRoutineChange(routineId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PreviewRoutineChangeMutationResult = NonNullable<
  Awaited<ReturnType<typeof previewRoutineChange>>
>;
export type PreviewRoutineChangeMutationBody = RoutineChangePreviewBody;
export type PreviewRoutineChangeMutationError = unknown;
export type PreviewRoutineChangeMutationVariables = {
  routineId: string;
  data: RoutineChangePreviewBody;
};

export const usePreviewRoutineChange = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof previewRoutineChange>>,
    TError,
    PreviewRoutineChangeMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof previewRoutineChange>>,
  TError,
  PreviewRoutineChangeMutationVariables,
  TContext
> => {
  return useMutation(getPreviewRoutineChangeMutationOptions(options));
};
export const getCancelRoutinePendingRotationUrl = (routineId: string) => {
  return `/api/v1/routines/${routineId}/pending/cancel`;
};

export const cancelRoutinePendingRotation = async (
  routineId: string,
  routinePendingCancelBody: RoutinePendingCancelBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<RoutineMutationResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<RoutineMutationResponseOutput>(
    getCancelRoutinePendingRotationUrl(routineId),
    {
      ...options,
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
      body: JSON.stringify(routinePendingCancelBody),
      schema: RoutineMutationResponseOutput,
    },
  );
};

export const getCancelRoutinePendingRotationMutationKey = () =>
  ['cancelRoutinePendingRotation'] as const;

export const getCancelRoutinePendingRotationMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof cancelRoutinePendingRotation>>,
    TError,
    CancelRoutinePendingRotationMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof cancelRoutinePendingRotation>>,
  TError,
  CancelRoutinePendingRotationMutationVariables,
  TContext
> => {
  const mutationKey = getCancelRoutinePendingRotationMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof cancelRoutinePendingRotation>>,
    CancelRoutinePendingRotationMutationVariables
  > = (props) => {
    const { routineId, data } = props ?? {};

    return cancelRoutinePendingRotation(routineId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CancelRoutinePendingRotationMutationResult = NonNullable<
  Awaited<ReturnType<typeof cancelRoutinePendingRotation>>
>;
export type CancelRoutinePendingRotationMutationBody = RoutinePendingCancelBody;
export type CancelRoutinePendingRotationMutationError = unknown;
export type CancelRoutinePendingRotationMutationVariables = {
  routineId: string;
  data: RoutinePendingCancelBody;
};

export const useCancelRoutinePendingRotation = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof cancelRoutinePendingRotation>>,
    TError,
    CancelRoutinePendingRotationMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof cancelRoutinePendingRotation>>,
  TError,
  CancelRoutinePendingRotationMutationVariables,
  TContext
> => {
  return useMutation(getCancelRoutinePendingRotationMutationOptions(options));
};
export const getConfirmRoutineChangeUrl = (routineId: string) => {
  return `/api/v1/routines/${routineId}`;
};

export const confirmRoutineChange = async (
  routineId: string,
  routineChangeConfirmBody: RoutineChangeConfirmBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<RoutineMutationResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<RoutineMutationResponseOutput>(getConfirmRoutineChangeUrl(routineId), {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(routineChangeConfirmBody),
    schema: RoutineMutationResponseOutput,
  });
};

export const getConfirmRoutineChangeMutationKey = () => ['confirmRoutineChange'] as const;

export const getConfirmRoutineChangeMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof confirmRoutineChange>>,
    TError,
    ConfirmRoutineChangeMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof confirmRoutineChange>>,
  TError,
  ConfirmRoutineChangeMutationVariables,
  TContext
> => {
  const mutationKey = getConfirmRoutineChangeMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof confirmRoutineChange>>,
    ConfirmRoutineChangeMutationVariables
  > = (props) => {
    const { routineId, data } = props ?? {};

    return confirmRoutineChange(routineId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ConfirmRoutineChangeMutationResult = NonNullable<
  Awaited<ReturnType<typeof confirmRoutineChange>>
>;
export type ConfirmRoutineChangeMutationBody = RoutineChangeConfirmBody;
export type ConfirmRoutineChangeMutationError = unknown;
export type ConfirmRoutineChangeMutationVariables = {
  routineId: string;
  data: RoutineChangeConfirmBody;
};

export const useConfirmRoutineChange = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof confirmRoutineChange>>,
    TError,
    ConfirmRoutineChangeMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof confirmRoutineChange>>,
  TError,
  ConfirmRoutineChangeMutationVariables,
  TContext
> => {
  return useMutation(getConfirmRoutineChangeMutationOptions(options));
};
export const getGetRoutineOverrideUrl = (overrideId: string) => {
  return `/api/v1/overrides/${overrideId}`;
};

export const getRoutineOverride = async (
  overrideId: string,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<OverrideDetailResponseOutput> => {
  return validatedFetch<OverrideDetailResponseOutput>(getGetRoutineOverrideUrl(overrideId), {
    ...options,
    method: 'GET',

    schema: OverrideDetailResponseOutput,
  });
};

export const getGetRoutineOverrideQueryKey = (overrideId: string) => {
  return [`/api/v1/overrides/${overrideId}`] as const;
};

export const getGetRoutineOverrideQueryOptions = <
  TData = Awaited<ReturnType<typeof getRoutineOverride>>,
  TError = unknown,
>(
  overrideId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getRoutineOverride>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRoutineOverrideQueryKey(overrideId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getRoutineOverride>>> = ({ signal }) =>
    getRoutineOverride(overrideId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: overrideId !== null && overrideId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getRoutineOverride>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetRoutineOverrideQueryResult = NonNullable<
  Awaited<ReturnType<typeof getRoutineOverride>>
>;
export type GetRoutineOverrideQueryError = unknown;

export function useGetRoutineOverride<
  TData = Awaited<ReturnType<typeof getRoutineOverride>>,
  TError = unknown,
>(
  overrideId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getRoutineOverride>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetRoutineOverrideQueryOptions(overrideId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getUpdateRoutineOverrideUrl = (overrideId: string) => {
  return `/api/v1/overrides/${overrideId}`;
};

export const updateRoutineOverride = async (
  overrideId: string,
  overrideUpdateBody: OverrideUpdateBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<OverrideMutationResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<OverrideMutationResponseOutput>(getUpdateRoutineOverrideUrl(overrideId), {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(overrideUpdateBody),
    schema: OverrideMutationResponseOutput,
  });
};

export const getUpdateRoutineOverrideMutationKey = () => ['updateRoutineOverride'] as const;

export const getUpdateRoutineOverrideMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateRoutineOverride>>,
    TError,
    UpdateRoutineOverrideMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateRoutineOverride>>,
  TError,
  UpdateRoutineOverrideMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateRoutineOverrideMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateRoutineOverride>>,
    UpdateRoutineOverrideMutationVariables
  > = (props) => {
    const { overrideId, data } = props ?? {};

    return updateRoutineOverride(overrideId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateRoutineOverrideMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateRoutineOverride>>
>;
export type UpdateRoutineOverrideMutationBody = OverrideUpdateBody;
export type UpdateRoutineOverrideMutationError = unknown;
export type UpdateRoutineOverrideMutationVariables = {
  overrideId: string;
  data: OverrideUpdateBody;
};

export const useUpdateRoutineOverride = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateRoutineOverride>>,
    TError,
    UpdateRoutineOverrideMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateRoutineOverride>>,
  TError,
  UpdateRoutineOverrideMutationVariables,
  TContext
> => {
  return useMutation(getUpdateRoutineOverrideMutationOptions(options));
};
export const getCreateRoutineOverrideUrl = (routineId: string) => {
  return `/api/v1/routines/${routineId}/overrides`;
};

export const createRoutineOverride = async (
  routineId: string,
  overrideCreateBody: OverrideCreateBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<OverrideMutationResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<OverrideMutationResponseOutput>(getCreateRoutineOverrideUrl(routineId), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(overrideCreateBody),
    schema: OverrideMutationResponseOutput,
  });
};

export const getCreateRoutineOverrideMutationKey = () => ['createRoutineOverride'] as const;

export const getCreateRoutineOverrideMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createRoutineOverride>>,
    TError,
    CreateRoutineOverrideMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createRoutineOverride>>,
  TError,
  CreateRoutineOverrideMutationVariables,
  TContext
> => {
  const mutationKey = getCreateRoutineOverrideMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createRoutineOverride>>,
    CreateRoutineOverrideMutationVariables
  > = (props) => {
    const { routineId, data } = props ?? {};

    return createRoutineOverride(routineId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateRoutineOverrideMutationResult = NonNullable<
  Awaited<ReturnType<typeof createRoutineOverride>>
>;
export type CreateRoutineOverrideMutationBody = OverrideCreateBody;
export type CreateRoutineOverrideMutationError = unknown;
export type CreateRoutineOverrideMutationVariables = {
  routineId: string;
  data: OverrideCreateBody;
};

export const useCreateRoutineOverride = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createRoutineOverride>>,
    TError,
    CreateRoutineOverrideMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof createRoutineOverride>>,
  TError,
  CreateRoutineOverrideMutationVariables,
  TContext
> => {
  return useMutation(getCreateRoutineOverrideMutationOptions(options));
};
export const getCancelRoutineOverrideUrl = (overrideId: string) => {
  return `/api/v1/overrides/${overrideId}/cancel`;
};

export const cancelRoutineOverride = async (
  overrideId: string,
  overrideCancelBody: OverrideCancelBody,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<OverrideMutationResponseOutput> => {
  const getHeaders = (
    h?: NonNullable<RequestInit['headers']>,
  ): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Array.isArray(h)) return Object.fromEntries(h);
    return h;
  };
  return validatedFetch<OverrideMutationResponseOutput>(getCancelRoutineOverrideUrl(overrideId), {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(overrideCancelBody),
    schema: OverrideMutationResponseOutput,
  });
};

export const getCancelRoutineOverrideMutationKey = () => ['cancelRoutineOverride'] as const;

export const getCancelRoutineOverrideMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof cancelRoutineOverride>>,
    TError,
    CancelRoutineOverrideMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof cancelRoutineOverride>>,
  TError,
  CancelRoutineOverrideMutationVariables,
  TContext
> => {
  const mutationKey = getCancelRoutineOverrideMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof cancelRoutineOverride>>,
    CancelRoutineOverrideMutationVariables
  > = (props) => {
    const { overrideId, data } = props ?? {};

    return cancelRoutineOverride(overrideId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CancelRoutineOverrideMutationResult = NonNullable<
  Awaited<ReturnType<typeof cancelRoutineOverride>>
>;
export type CancelRoutineOverrideMutationBody = OverrideCancelBody;
export type CancelRoutineOverrideMutationError = unknown;
export type CancelRoutineOverrideMutationVariables = {
  overrideId: string;
  data: OverrideCancelBody;
};

export const useCancelRoutineOverride = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof cancelRoutineOverride>>,
    TError,
    CancelRoutineOverrideMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof validatedFetch>;
}): UseMutationResult<
  Awaited<ReturnType<typeof cancelRoutineOverride>>,
  TError,
  CancelRoutineOverrideMutationVariables,
  TContext
> => {
  return useMutation(getCancelRoutineOverrideMutationOptions(options));
};
export const getGetOverrideTimeOptionsUrl = (params: GetOverrideTimeOptionsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/time-options?${stringifiedParams}`
    : `/api/v1/time-options`;
};

export const getOverrideTimeOptions = async (
  params: GetOverrideTimeOptionsParams,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<OverrideTimeOptionsResponseOutput> => {
  return validatedFetch<OverrideTimeOptionsResponseOutput>(getGetOverrideTimeOptionsUrl(params), {
    ...options,
    method: 'GET',

    schema: OverrideTimeOptionsResponseOutput,
  });
};

export const getGetOverrideTimeOptionsQueryKey = (params?: GetOverrideTimeOptionsParams) => {
  return [`/api/v1/time-options`, ...(params ? [params] : [])] as const;
};

export const getGetOverrideTimeOptionsQueryOptions = <
  TData = Awaited<ReturnType<typeof getOverrideTimeOptions>>,
  TError = unknown,
>(
  params: GetOverrideTimeOptionsParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getOverrideTimeOptions>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetOverrideTimeOptionsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOverrideTimeOptions>>> = ({ signal }) =>
    getOverrideTimeOptions(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOverrideTimeOptions>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetOverrideTimeOptionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOverrideTimeOptions>>
>;
export type GetOverrideTimeOptionsQueryError = unknown;

export function useGetOverrideTimeOptions<
  TData = Awaited<ReturnType<typeof getOverrideTimeOptions>>,
  TError = unknown,
>(
  params: GetOverrideTimeOptionsParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getOverrideTimeOptions>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetOverrideTimeOptionsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getGetTeamTimelineUrl = (teamId: string, params: GetTeamTimelineParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/teams/${teamId}/timeline?${stringifiedParams}`
    : `/api/v1/teams/${teamId}/timeline`;
};

export const getTeamTimeline = async (
  teamId: string,
  params: GetTeamTimelineParams,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<TimelineResponseOutput> => {
  return validatedFetch<TimelineResponseOutput>(getGetTeamTimelineUrl(teamId, params), {
    ...options,
    method: 'GET',

    schema: TimelineResponseOutput,
  });
};

export const getGetTeamTimelineQueryKey = (teamId: string, params?: GetTeamTimelineParams) => {
  return [`/api/v1/teams/${teamId}/timeline`, ...(params ? [params] : [])] as const;
};

export const getGetTeamTimelineQueryOptions = <
  TData = Awaited<ReturnType<typeof getTeamTimeline>>,
  TError = unknown,
>(
  teamId: string,
  params: GetTeamTimelineParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getTeamTimeline>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTeamTimelineQueryKey(teamId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTeamTimeline>>> = ({ signal }) =>
    getTeamTimeline(teamId, params, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: teamId !== null && teamId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getTeamTimeline>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetTeamTimelineQueryResult = NonNullable<Awaited<ReturnType<typeof getTeamTimeline>>>;
export type GetTeamTimelineQueryError = unknown;

export function useGetTeamTimeline<
  TData = Awaited<ReturnType<typeof getTeamTimeline>>,
  TError = unknown,
>(
  teamId: string,
  params: GetTeamTimelineParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getTeamTimeline>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetTeamTimelineQueryOptions(teamId, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getGetScheduleTimelineUrl = (
  scheduleId: string,
  params: GetScheduleTimelineParams,
) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/schedules/${scheduleId}/timeline?${stringifiedParams}`
    : `/api/v1/schedules/${scheduleId}/timeline`;
};

export const getScheduleTimeline = async (
  scheduleId: string,
  params: GetScheduleTimelineParams,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<TimelineResponseOutput> => {
  return validatedFetch<TimelineResponseOutput>(getGetScheduleTimelineUrl(scheduleId, params), {
    ...options,
    method: 'GET',

    schema: TimelineResponseOutput,
  });
};

export const getGetScheduleTimelineQueryKey = (
  scheduleId: string,
  params?: GetScheduleTimelineParams,
) => {
  return [`/api/v1/schedules/${scheduleId}/timeline`, ...(params ? [params] : [])] as const;
};

export const getGetScheduleTimelineQueryOptions = <
  TData = Awaited<ReturnType<typeof getScheduleTimeline>>,
  TError = unknown,
>(
  scheduleId: string,
  params: GetScheduleTimelineParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getScheduleTimeline>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetScheduleTimelineQueryKey(scheduleId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getScheduleTimeline>>> = ({ signal }) =>
    getScheduleTimeline(scheduleId, params, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: scheduleId !== null && scheduleId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getScheduleTimeline>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetScheduleTimelineQueryResult = NonNullable<
  Awaited<ReturnType<typeof getScheduleTimeline>>
>;
export type GetScheduleTimelineQueryError = unknown;

export function useGetScheduleTimeline<
  TData = Awaited<ReturnType<typeof getScheduleTimeline>>,
  TError = unknown,
>(
  scheduleId: string,
  params: GetScheduleTimelineParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getScheduleTimeline>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetScheduleTimelineQueryOptions(scheduleId, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getGetTeamDutyUrl = (teamId: string, params?: GetTeamDutyParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/teams/${teamId}/duty?${stringifiedParams}`
    : `/api/v1/teams/${teamId}/duty`;
};

export const getTeamDuty = async (
  teamId: string,
  params?: GetTeamDutyParams,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<AugmentedZodDtoOutput> => {
  return validatedFetch<AugmentedZodDtoOutput>(getGetTeamDutyUrl(teamId, params), {
    ...options,
    method: 'GET',

    schema: AugmentedZodDtoOutput,
  });
};

export const getGetTeamDutyQueryKey = (teamId: string, params?: GetTeamDutyParams) => {
  return [`/api/v1/teams/${teamId}/duty`, ...(params ? [params] : [])] as const;
};

export const getGetTeamDutyQueryOptions = <
  TData = Awaited<ReturnType<typeof getTeamDuty>>,
  TError = unknown,
>(
  teamId: string,
  params?: GetTeamDutyParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getTeamDuty>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTeamDutyQueryKey(teamId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTeamDuty>>> = ({ signal }) =>
    getTeamDuty(teamId, params, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: teamId !== null && teamId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getTeamDuty>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetTeamDutyQueryResult = NonNullable<Awaited<ReturnType<typeof getTeamDuty>>>;
export type GetTeamDutyQueryError = unknown;

export function useGetTeamDuty<TData = Awaited<ReturnType<typeof getTeamDuty>>, TError = unknown>(
  teamId: string,
  params?: GetTeamDutyParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getTeamDuty>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetTeamDutyQueryOptions(teamId, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getGetOwnDutyCalendarUrl = (params: GetOwnDutyCalendarParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/me/on-call/calendar?${stringifiedParams}`
    : `/api/v1/me/on-call/calendar`;
};

export const getOwnDutyCalendar = async (
  params: GetOwnDutyCalendarParams,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<OwnDutyResponseOutput> => {
  return validatedFetch<OwnDutyResponseOutput>(getGetOwnDutyCalendarUrl(params), {
    ...options,
    method: 'GET',

    schema: OwnDutyResponseOutput,
  });
};

export const getGetOwnDutyCalendarQueryKey = (params?: GetOwnDutyCalendarParams) => {
  return [`/api/v1/me/on-call/calendar`, ...(params ? [params] : [])] as const;
};

export const getGetOwnDutyCalendarQueryOptions = <
  TData = Awaited<ReturnType<typeof getOwnDutyCalendar>>,
  TError = unknown,
>(
  params: GetOwnDutyCalendarParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getOwnDutyCalendar>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetOwnDutyCalendarQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOwnDutyCalendar>>> = ({ signal }) =>
    getOwnDutyCalendar(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOwnDutyCalendar>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetOwnDutyCalendarQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOwnDutyCalendar>>
>;
export type GetOwnDutyCalendarQueryError = unknown;

export function useGetOwnDutyCalendar<
  TData = Awaited<ReturnType<typeof getOwnDutyCalendar>>,
  TError = unknown,
>(
  params: GetOwnDutyCalendarParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getOwnDutyCalendar>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetOwnDutyCalendarQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getGetOwnDutyUrl = (params: GetOwnDutyParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/me/on-call?${stringifiedParams}`
    : `/api/v1/me/on-call`;
};

export const getOwnDuty = async (
  params: GetOwnDutyParams,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<OwnDutyResponseOutput> => {
  return validatedFetch<OwnDutyResponseOutput>(getGetOwnDutyUrl(params), {
    ...options,
    method: 'GET',

    schema: OwnDutyResponseOutput,
  });
};

export const getGetOwnDutyQueryKey = (params?: GetOwnDutyParams) => {
  return [`/api/v1/me/on-call`, ...(params ? [params] : [])] as const;
};

export const getGetOwnDutyQueryOptions = <
  TData = Awaited<ReturnType<typeof getOwnDuty>>,
  TError = unknown,
>(
  params: GetOwnDutyParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getOwnDuty>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetOwnDutyQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOwnDuty>>> = ({ signal }) =>
    getOwnDuty(params, { signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOwnDuty>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetOwnDutyQueryResult = NonNullable<Awaited<ReturnType<typeof getOwnDuty>>>;
export type GetOwnDutyQueryError = unknown;

export function useGetOwnDuty<TData = Awaited<ReturnType<typeof getOwnDuty>>, TError = unknown>(
  params: GetOwnDutyParams,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getOwnDuty>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetOwnDutyQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getGetEligibleScheduleAgentsUrl = (teamId: string) => {
  return `/api/v1/teams/${teamId}/schedule-agents`;
};

export const getEligibleScheduleAgents = async (
  teamId: string,
  options?: Parameters<typeof validatedFetch>[1],
): Promise<EligibleScheduleAgentsResponseOutput> => {
  return validatedFetch<EligibleScheduleAgentsResponseOutput>(
    getGetEligibleScheduleAgentsUrl(teamId),
    {
      ...options,
      method: 'GET',

      schema: EligibleScheduleAgentsResponseOutput,
    },
  );
};

export const getGetEligibleScheduleAgentsQueryKey = (teamId: string) => {
  return [`/api/v1/teams/${teamId}/schedule-agents`] as const;
};

export const getGetEligibleScheduleAgentsQueryOptions = <
  TData = Awaited<ReturnType<typeof getEligibleScheduleAgents>>,
  TError = unknown,
>(
  teamId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getEligibleScheduleAgents>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetEligibleScheduleAgentsQueryKey(teamId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getEligibleScheduleAgents>>> = ({
    signal,
  }) => getEligibleScheduleAgents(teamId, { signal, ...requestOptions });

  return {
    queryKey,
    queryFn,
    enabled: teamId !== null && teamId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getEligibleScheduleAgents>>, TError, TData> & {
    queryKey: QueryKey;
  };
};

export type GetEligibleScheduleAgentsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getEligibleScheduleAgents>>
>;
export type GetEligibleScheduleAgentsQueryError = unknown;

export function useGetEligibleScheduleAgents<
  TData = Awaited<ReturnType<typeof getEligibleScheduleAgents>>,
  TError = unknown,
>(
  teamId: string,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getEligibleScheduleAgents>>, TError, TData>;
    request?: SecondParameter<typeof validatedFetch>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetEligibleScheduleAgentsQueryOptions(teamId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}

export const getGetOwnScheduleTeamsUrl = () => {
  return `/api/v1/me/on-call/teams`;
};

export const getOwnScheduleTeams = async (
  options?: Parameters<typeof validatedFetch>[1],
): Promise<OwnScheduleTeamsResponseOutput> => {
  return validatedFetch<OwnScheduleTeamsResponseOutput>(getGetOwnScheduleTeamsUrl(), {
    ...options,
    method: 'GET',

    schema: OwnScheduleTeamsResponseOutput,
  });
};

export const getGetOwnScheduleTeamsQueryKey = () => {
  return [`/api/v1/me/on-call/teams`] as const;
};

export const getGetOwnScheduleTeamsQueryOptions = <
  TData = Awaited<ReturnType<typeof getOwnScheduleTeams>>,
  TError = unknown,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getOwnScheduleTeams>>, TError, TData>;
  request?: SecondParameter<typeof validatedFetch>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetOwnScheduleTeamsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOwnScheduleTeams>>> = ({ signal }) =>
    getOwnScheduleTeams({ signal, ...requestOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOwnScheduleTeams>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetOwnScheduleTeamsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOwnScheduleTeams>>
>;
export type GetOwnScheduleTeamsQueryError = unknown;

export function useGetOwnScheduleTeams<
  TData = Awaited<ReturnType<typeof getOwnScheduleTeams>>,
  TError = unknown,
>(options?: {
  query?: UseQueryOptions<Awaited<ReturnType<typeof getOwnScheduleTeams>>, TError, TData>;
  request?: SecondParameter<typeof validatedFetch>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetOwnScheduleTeamsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  return withQueryKey(query, queryOptions.queryKey);
}
